import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Clock, FolderOpen, MessageSquare, Plus, Upload } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useChatHistory } from '../../components/ChatHistory';
import { PageBody } from '../../components/layout';
import { PageHeader } from '../../components/PageHeader';
import { ProjectCard } from '../../components/ProjectList/ProjectCard';
import { useAuth } from '../../contexts/authContext';
import { useFeatureFlag, useFeatureFlagsReady } from '../../contexts/featureFlagContext';
import { useProjects } from '../../hooks/useProjects';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { formatRelativeTime } from '../../utils/time';
import { DashboardRedesign } from './DashboardRedesign';
import { DeliverySummaryCard } from './DeliverySummaryCard';
import { MyAssignedActionsCard } from './MyAssignedActionsCard';
function QuickAction({ icon, label, href }) {
    return /*#__PURE__*/ _jsx(Link, {
        to: href,
        children: /*#__PURE__*/ _jsxs(SpecraButton, {
            variant: "secondary",
            className: "flex h-24 w-full flex-col items-center justify-center gap-2",
            children: [
                icon,
                /*#__PURE__*/ _jsx("span", {
                    className: "text-sm font-medium",
                    children: label
                })
            ]
        })
    });
}
export default function Dashboard() {
    const flagsReady = useFeatureFlagsReady();
    const redesignEnabled = useFeatureFlag('specra_redesign_dashboard');
    if (flagsReady && redesignEnabled) {
        return /*#__PURE__*/ _jsx(DashboardRedesign, {});
    }
    return /*#__PURE__*/ _jsx(DashboardLegacy, {});
}
function DashboardLegacy() {
    const { user } = useAuth();
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    // Hide breadcrumbs on dashboard
    useEffect(()=>{
        setShowBreadcrumbs(false);
        return ()=>setShowBreadcrumbs(true);
    }, [
        setShowBreadcrumbs
    ]);
    // Fetch recent projects
    const { data: projectsData, isLoading: projectsLoading } = useProjects({
        status: 'active'
    });
    // Get first 6 projects for display
    const recentProjects = projectsData?.projects.slice(0, 6) ?? [];
    // Fetch chat history (shared ['chats'] query with the History page, so the
    // cache is warm when navigating between them). Slice to the 5 most recent.
    const { chats, isLoading: chatsLoading } = useChatHistory();
    const recentChats = useMemo(()=>[
            ...chats
        ].sort((a, b)=>new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 5), [
        chats
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-xl font-semibold",
                            children: "Dashboard"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Welcome back. Here's what's happening with your projects."
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    /*#__PURE__*/ _jsxs("section", {
                        className: "mb-8",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-lg font-medium mb-4",
                                children: "Quick Actions"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid grid-cols-2 md:grid-cols-4 gap-4",
                                children: [
                                    /*#__PURE__*/ _jsx(QuickAction, {
                                        icon: /*#__PURE__*/ _jsx(MessageSquare, {
                                            className: "h-6 w-6"
                                        }),
                                        label: "New Chat",
                                        href: "/assistant"
                                    }),
                                    /*#__PURE__*/ _jsx(QuickAction, {
                                        icon: /*#__PURE__*/ _jsx(Plus, {
                                            className: "h-6 w-6"
                                        }),
                                        label: "New Project",
                                        href: "/projects"
                                    }),
                                    /*#__PURE__*/ _jsx(QuickAction, {
                                        icon: /*#__PURE__*/ _jsx(Upload, {
                                            className: "h-6 w-6"
                                        }),
                                        label: "Upload Document",
                                        href: "/documents/upload"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(DeliverySummaryCard, {
                        companyId: user?.tenant_id
                    }),
                    /*#__PURE__*/ _jsx(MyAssignedActionsCard, {}),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "mb-8",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between mb-4",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-lg font-medium",
                                        children: "Recent Projects"
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: "/projects",
                                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "ghost",
                                            size: "sm",
                                            children: "View All"
                                        })
                                    })
                                ]
                            }),
                            projectsLoading ? /*#__PURE__*/ _jsx("div", {
                                className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
                                "aria-busy": "true",
                                children: [
                                    1,
                                    2,
                                    3
                                ].map((i)=>/*#__PURE__*/ _jsx("div", {
                                        className: "h-32 bg-muted rounded-lg animate-pulse"
                                    }, i))
                            }) : recentProjects.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                                className: "text-center py-12 bg-muted/50 rounded-lg",
                                children: [
                                    /*#__PURE__*/ _jsx(FolderOpen, {
                                        className: "h-10 w-10 mx-auto mb-3 text-muted-foreground"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-muted-foreground mb-4",
                                        children: "No projects yet"
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: "/projects",
                                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "secondary",
                                            size: "sm",
                                            children: "Create Your First Project"
                                        })
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
                                children: recentProjects.map((project)=>/*#__PURE__*/ _jsx(ProjectCard, {
                                        project: project
                                    }, project.projectId))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        "data-testid": Testids.dashboard.recentChats,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between mb-4",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-lg font-medium",
                                        children: "Recent Chats"
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: "/history",
                                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "ghost",
                                            size: "sm",
                                            children: "View All"
                                        })
                                    })
                                ]
                            }),
                            chatsLoading ? /*#__PURE__*/ _jsx("div", {
                                className: "space-y-2",
                                "aria-busy": "true",
                                children: [
                                    1,
                                    2,
                                    3
                                ].map((i)=>/*#__PURE__*/ _jsx("div", {
                                        className: "h-16 bg-muted rounded-lg animate-pulse"
                                    }, i))
                            }) : recentChats.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                                className: "text-center py-8 bg-muted/50 rounded-lg",
                                children: [
                                    /*#__PURE__*/ _jsx(MessageSquare, {
                                        className: "h-8 w-8 mx-auto mb-2 text-muted-foreground"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-muted-foreground",
                                        children: "Your recent conversations will appear here"
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "space-y-2",
                                children: recentChats.map((chat)=>/*#__PURE__*/ _jsxs(Link, {
                                        to: `/assistant/${chat.chatId}`,
                                        "data-testid": Testids.dashboard.recentChatItem,
                                        className: "flex items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted transition-colors",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-3 min-w-0",
                                                children: [
                                                    /*#__PURE__*/ _jsx(MessageSquare, {
                                                        className: "h-4 w-4 flex-shrink-0 text-muted-foreground"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "font-medium truncate",
                                                        children: chat.title
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1.5 text-xs text-muted-foreground flex-shrink-0",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Clock, {
                                                        className: "h-3 w-3"
                                                    }),
                                                    /*#__PURE__*/ _jsx("time", {
                                                        dateTime: chat.updatedAt,
                                                        children: formatRelativeTime(chat.updatedAt)
                                                    })
                                                ]
                                            })
                                        ]
                                    }, chat.chatId))
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
